import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Alert, Paper, Stack, IconButton, Tooltip
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { translateAndSave } from '../api';

const LANGUAGES = [
  { code: 'en', name: 'English',   flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi',     flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati',  flag: '🇮🇳' },
];

export default function Translator({ meeting, onTranslationDone, showSnackbar }) {
  const [targetLang, setTargetLang]         = useState('hi');
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  const cachedText = meeting?.translated_text?.[targetLang];

  const handleTranslate = async () => {
    if (!meeting?.full_text) return;
    setError(''); setLoading(true); setTranslatedText('');
    try {
      const text = await translateAndSave(meeting.meeting_id, meeting.full_text, targetLang);
      setTranslatedText(text);
      onTranslationDone(targetLang, text);
      showSnackbar(`Translated to ${LANGUAGES.find(l => l.code === targetLang)?.name}!`, 'success');
    } catch (err) {
      setError(err.message || 'Translation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const text = translatedText || cachedText;
    if (text) { navigator.clipboard.writeText(text); showSnackbar('Copied to clipboard!', 'success'); }
  };

  const displayText = translatedText || cachedText || '';
  const langInfo    = LANGUAGES.find(l => l.code === targetLang);

  return (
    <Box className="fade-in-up">

      {/* ── Controls card ── */}
      <Card className="glass-card" sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>

          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TranslateIcon sx={{ fontSize: 18, color: '#06B6D4' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: 15 }}>
                Translate Transcript
              </Typography>
              <Typography variant="caption" sx={{ color: '#475569' }}>
                Powered by Google Translate
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>

            {/* Language selector */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ color: '#64748B', fontSize: 13 }}>Target Language</InputLabel>
              <Select
                value={targetLang}
                onChange={(e) => { setTargetLang(e.target.value); setTranslatedText(''); setError(''); }}
                label="Target Language"
                id="select-language"
                sx={{
                  borderRadius: '12px',
                  background: 'rgba(12,12,24,0.7)',
                  '& .MuiSelect-select': { py: 1.3, fontSize: 14 },
                }}
              >
                {LANGUAGES.map(lang => (
                  <MenuItem key={lang.code} value={lang.code} sx={{ gap: 1 }}>
                    <span style={{ fontSize: 18 }}>{lang.flag}</span>
                    <span style={{ fontWeight: 600 }}>{lang.name}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Translate button */}
            <Button
              variant="contained"
              onClick={handleTranslate}
              disabled={loading || !meeting?.full_text}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />}
              id="btn-translate"
              sx={{
                borderRadius: '12px', px: 3.5, py: 1.2, fontWeight: 700,
                background: 'linear-gradient(135deg, #06B6D4, #0284C7)',
                boxShadow: '0 4px 20px rgba(6,182,212,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #22D3EE, #0EA5E9)', boxShadow: '0 6px 28px rgba(6,182,212,0.45)' },
              }}
            >
              {loading ? 'Translating…' : `Translate to ${langInfo?.name || ''}`}
            </Button>

          </Stack>

          {error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}
          {cachedText && !translatedText && (
            <Alert severity="info" sx={{ mt: 2.5 }}>
              Showing cached translation. Click "Translate" to refresh.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Side-by-side layout on desktop ── */}
      {displayText && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>

          {/* Source (original) */}
          <Card className="glass-card" sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ px: 1.2, py: 0.4, borderRadius: '8px', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.2)' }}>
                    <Typography variant="caption" sx={{ color: '#A78BFA', fontWeight: 800, fontSize: 10, fontFamily: '"JetBrains Mono", monospace', letterSpacing: 0.5 }}>
                      SOURCE · AUTO
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.08)', maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#94A3B8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {meeting?.full_text?.slice(0, 600)}{(meeting?.full_text?.length || 0) > 600 ? '…' : ''}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Translated */}
          <Card className="glass-card" sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ px: 1.2, py: 0.4, borderRadius: '8px', background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.2)' }}>
                    <Typography variant="caption" sx={{ color: '#22D3EE', fontWeight: 800, fontSize: 10, fontFamily: '"JetBrains Mono", monospace', letterSpacing: 0.5 }}>
                      {langInfo?.flag} {langInfo?.name?.toUpperCase()}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.8}>
                  <Tooltip title="Refresh translation" arrow>
                    <IconButton size="small" onClick={handleTranslate} disabled={loading} sx={{ border: '1px solid rgba(6,182,212,0.2)', borderRadius: '8px', color: '#06B6D4', '&:hover': { background: 'rgba(6,182,212,0.1)' } }}>
                      <RefreshIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy translation" arrow>
                    <IconButton size="small" onClick={handleCopy} sx={{ border: '1px solid rgba(6,182,212,0.2)', borderRadius: '8px', color: '#06B6D4', '&:hover': { background: 'rgba(6,182,212,0.1)' } }}>
                      <ContentCopyIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.1)', borderLeft: '3px solid #06B6D4', maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body1" sx={{ lineHeight: 2, color: '#E2E8F0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {displayText}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

        </Stack>
      )}
    </Box>
  );
}
